import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import useAuth from '../hooks/useAuth';

export const ActiveCondominioContext = createContext();

/**
 * ActiveCondominioProvider - Context para gerenciar condomínio ativo globalmente
 * 
 * Substitui localStorage por React state reativo
 * Sincroniza automaticamente com useAuth (lista de condominios disponíveis)
 * 
 * Fornece:
 * - activeCondominio: string | null
 * - setActiveCondominio: (novoId) => void
 * - condominios: array
 */
export const ActiveCondominioProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeCondominio, setActiveCondominioState] = useState(null);

  useEffect(() => {
    const handleAuthTokenChanged = () => {
      setActiveCondominioState(null);
    };

    window.addEventListener('auth-token-changed', handleAuthTokenChanged);
    return () => {
      window.removeEventListener('auth-token-changed', handleAuthTokenChanged);
    };
  }, []);

  const condominios = useMemo(() => user?.condominios || [], [user]);
  const allowedIds = useMemo(
    () => condominios.map((c) => String(c.id)),
    [condominios]
  );

  // ✅ Inicializar activeCondominio quando user carrega
  useEffect(() => {
    if (authLoading) {
      // Enquanto autenticando, não fazer nada
      return;
    }

    if (!user) {
      // User fez logout
      setActiveCondominioState(null);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    // User fez login ou mudou - garantir activeCondominio válido
    if (activeCondominio && allowedIds.includes(String(activeCondominio))) {
      return;
    }

    const next = allowedIds[0] || null;
    setActiveCondominioState(next);
  }, [authLoading, user, allowedIds, activeCondominio]);

  const setActiveCondominio = useCallback((novoCondominioId) => {
    if (!novoCondominioId) return;
    const normalized = String(novoCondominioId);

    // Validar se condomínio está na lista permitida
    if (allowedIds.length > 0 && !allowedIds.includes(normalized)) {
      console.warn(`Condomínio ${normalized} não autorizado`);
      return;
    }

    setActiveCondominioState(normalized);
  }, [allowedIds]);

  const value = useMemo(() => ({
    activeCondominio,
    setActiveCondominio,
    condominios,
    loading: authLoading
  }), [activeCondominio, setActiveCondominio, condominios, authLoading]);

  return (
    <ActiveCondominioContext.Provider value={value}>
      {children}
    </ActiveCondominioContext.Provider>
  );
};
